import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Catalog of QA services (lines of business) — Collections, Customer Service,
 * Insurance Verification, Field Services, etc. A campaign is linked to a service, and a
 * scorecard can be scoped per service. Campaigns use the Global scorecard until a
 * service- or campaign-specific one is created.
 */
const CallQaServicesPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.scorecards.read');
  const canManage = permissions.includes('call_qa.scorecards.manage');
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['callQaServices'],
    queryFn: () => apiClient.callQa.listServices(true),
    enabled: canRead,
  });
  const services: any[] = Array.isArray(data?.data?.data) ? data!.data!.data : [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['callQaServices'] });

  const createMutation = useMutation({
    mutationFn: () => apiClient.callQa.createService({ name: newName.trim() }),
    onSuccess: () => { setNewName(''); setError(null); invalidate(); },
    onError: (err: any) => setError(err?.response?.data?.message || 'Create failed'),
  });

  const updateMutation = useMutation({
    mutationFn: (p: { id: string; patch: Record<string, unknown> }) =>
      apiClient.callQa.updateService(p.id, p.patch),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (err: any) => setError(err?.response?.data?.message || 'Update failed'),
  });

  if (!canRead) {
    return <Alert severity="warning">Services require call_qa.scorecards.read.</Alert>;
  }
  if (isLoading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Service Catalog</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lines of business a campaign can belong to. Build a scorecard per service on the
        Scorecards tab; campaigns use the Global scorecard until one exists.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {canManage && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <TextField
            size="small"
            label="New service name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) createMutation.mutate(); }}
          />
          <Button
            variant="contained"
            disabled={!newName.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Add Service
          </Button>
        </Stack>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell sx={{ width: 220 }}>Code</TableCell>
              <TableCell sx={{ width: 120 }}>Enabled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  {canManage ? (
                    <TextField
                      size="small"
                      fullWidth
                      defaultValue={s.name}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== s.name) updateMutation.mutate({ id: s.id, patch: { name: v } });
                      }}
                    />
                  ) : s.name}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={s.code} variant="outlined" />
                </TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    checked={Boolean(s.enabled)}
                    disabled={!canManage}
                    onChange={(e) => updateMutation.mutate({ id: s.id, patch: { enabled: e.target.checked } })}
                  />
                </TableCell>
              </TableRow>
            ))}
            {services.length === 0 && (
              <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">No services yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CallQaServicesPage;
